import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QaiFieldComponent, QaiTextareaComponent, QaiTrimLeadingDirective, TextareaAutosizeDirective, QaiPasteCleanDirective } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';

@Component({
  selector: 'simulation-prompts',
  standalone: true,
  templateUrl: './simulation-prompt.component.html',
  styleUrls: ['./simulation-prompt.component.scss'],
  imports: [
    QaiFieldComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    DetailsSectionComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective
],
})
export class SimulationPromptComponent {
  form!: FormGroup;
  converseSection!: FormGroup;
  callUuid: string = '';

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.loadFormAndSetupAutoSave();
    this.setReadOnlyMode();
  }

  private setReadOnlyMode() {
    this.authorReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.conversePromptGroup?.get('promptText')
    );
  }

  get conversePromptGroup(): FormGroup | undefined {
    return this.converseSection ?? undefined;
  }

  private loadFormAndSetupAutoSave(): void {
    this.authorReactiveFormService.getForm(this.callUuid).subscribe((form) => {
      this.form = form.get(SimulationConstants.CONTENT_SECTION) as FormGroup;
      this.converseSection = form.get(
        SimulationConstants.CONVERSE_PROMPT_PATH
      ) as FormGroup;
      this.setupAutoSave(
        this.converseSection,
        SimulationConstants.CONVERSE_PROMPT_PATH
      );
    });
  }

  private setupAutoSave(section: FormGroup, formPath: string): void {
    const promptTextControl = section?.get('promptText');

    if (!promptTextControl) return;

    let previousValue: string = promptTextControl.getRawValue();

    promptTextControl.valueChanges
      .pipe(
        skip(1), // ignore initial form init change
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((newVal) => {
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue = section.getRawValue();

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        const readOnly = this.authorReactiveFormService.isReadOnly(
          this.callUuid
        );

        this.authorReactiveFormService.patchForm(
          this.callUuid,
          formPath,
          updatedValue
        );

        if (updatedValue && !readOnly) {
          this.triggerPromptSave(this.callUuid, updatedValue, formPath);
        }
      });
  }

  private triggerPromptSave(
    callUuid: string,
    prompt: ISimulationPrompt,
    formPath: string
  ): void {
    const payload = { prompts: [prompt] };

    const request$ = prompt.uuid
      ? this.authorService.updateSimulationPrompts(callUuid, payload.prompts)
      : this.authorService.createSimulationPrompts(callUuid, payload.prompts);

    request$.subscribe((response) => {
      const updatedPrompt = response?.prompts?.[0];
      if (!updatedPrompt?.uuid) return;

      this.authorReactiveFormService.patchForm(callUuid, formPath, {
        uuid: updatedPrompt.promptText === '' ? null : updatedPrompt.uuid,
      });

      this.form.updateValueAndValidity();
    });
  }
}
